/*
  Google Analytics 4 cho facego.site.

  Không dán đoạn `<script>` của Google vào index.html: CSP của trang (nginx.conf.template)
  không cho script nội tuyến, nên đoạn khởi tạo nằm ở đây, còn `gtag.js` nạp từ
  googletagmanager.com (đã mở trong `script-src`).

  Lượt xem trang gửi tay, không để GA4 tự đếm theo lịch sử trình duyệt: URL của trang
  này mang token đặt lại mật khẩu, mã xác minh email, `state` / `id_token` của đăng nhập
  Google và mã giới thiệu — tự đếm là gửi nguyên các thứ đó sang Google. Ở luồng dữ
  liệu của GA4 phải tắt "Page changes based on browser history events" (Enhanced
  measurement → Page views), nếu không mỗi lần chuyển trang bị đếm hai lần.

  Referrer cũng lọc: rời `/reset-password?token=…` bằng một lần tải trang là trang sau
  có `document.referrer` mang nguyên token, và gtag tự gửi nó (`dr`) nếu không ghi đè.
  `page_location` / `page_referrer` đặt bằng `set` chứ không ở `config`, để cả sự kiện
  tự động (cuộn trang, engagement) cũng mang URL đã lọc của trang hiện tại.
*/

export const GA_MEASUREMENT_ID = "G-4CC2E6ZVGE";

/** Chỉ bản chạy thật mới gửi số liệu; dev, preview, chế độ mock và test thì không. */
const PRODUCTION_HOSTS = new Set(["facego.site", "www.facego.site"]);

/** Query được giữ lại: nguồn chiến dịch. Mọi tham số khác không rời trình duyệt. */
const KEPT_PARAM = /^(utm_[a-z_]+|gclid|fbclid)$/i;

type Gtag = (...args: unknown[]) => void;

interface AnalyticsWindow {
  dataLayer?: unknown[];
  document: Document;
  gtag?: Gtag;
  location: Location;
}

let installed = false;
/** URL đã lọc của lượt xem trước — làm referrer cho lượt chuyển trang trong app. */
let previousLocation: string | null = null;

/** `https://facego.site/reset-password?token=…#x` → `https://facego.site/reset-password`. */
export function analyticsPageLocation(href: string): string {
  const url = new URL(href);
  const kept = new URLSearchParams();
  url.searchParams.forEach((value, key) => {
    if (KEPT_PARAM.test(key)) kept.append(key, value);
  });
  const query = kept.toString();
  return `${url.origin}${url.pathname}${query ? `?${query}` : ""}`;
}

/** `document.referrer` qua cùng bộ lọc; rỗng hoặc không phải URL thì là chuỗi rỗng. */
export function analyticsReferrer(referrer: string): string {
  if (!/^https?:\/\//i.test(referrer)) return "";
  try {
    return analyticsPageLocation(referrer);
  } catch {
    return "";
  }
}

function pageContext(win: AnalyticsWindow) {
  return {
    page_location: analyticsPageLocation(win.location.href),
    page_referrer: previousLocation ?? analyticsReferrer(win.document.referrer),
  };
}

/** Gắn GA4 một lần, lúc app khởi động. Trả `false` khi không phải facego.site. */
export function installAnalytics(win: AnalyticsWindow = window as unknown as AnalyticsWindow): boolean {
  if (installed) return true;
  if (!PRODUCTION_HOSTS.has(win.location.hostname)) return false;
  installed = true;
  const script = win.document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  win.document.head.appendChild(script);
  win.dataLayer = win.dataLayer || [];
  // Đúng dạng hàm của snippet Google: gtag.js đọc `arguments`, không đọc mảng.
  win.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    win.dataLayer!.push(arguments);
  };
  win.gtag("js", new Date());
  win.gtag("set", pageContext(win));
  win.gtag("config", GA_MEASUREMENT_ID, {
    allow_ad_personalization_signals: false,
    allow_google_signals: false,
    send_page_view: false,
  });
  return true;
}

/** Một lượt xem trang với URL đã lọc. `App` gọi mỗi lần đường dẫn đổi, kể cả lần tải đầu. */
export function trackPageView(win: AnalyticsWindow = window as unknown as AnalyticsWindow): void {
  if (!installed || !win.gtag) return;
  const context = pageContext(win);
  win.gtag("set", context);
  win.gtag("event", "page_view", { ...context, page_title: win.document.title });
  previousLocation = context.page_location;
}

/** Chỉ cho test: đưa module về trạng thái chưa gắn. */
export function resetAnalyticsForTest(): void {
  installed = false;
  previousLocation = null;
}
